import {
  Alert,
  Button,
  Divider,
  Flex,
  Popconfirm,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  DisconnectOutlined,
  InboxOutlined,
  RedoOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useArchiveChatConversation,
  useChatConversation,
  useMarkChatConversationHandled,
  useMarkChatConversationUnread,
  useMoveChatConversation,
  useUnarchiveChatConversation,
  useUnlinkChatConversation,
} from "../../../../lib/hooks/useChat";
import {
  chatChannelConfig,
  chatConversationStatusConfig,
} from "../data/chatChannelConfig";
import { ChatThread } from "./ChatThread";
import { ChatComposer } from "./ChatComposer";
import { ChatMoveDossierButton } from "./ChatMoveDossierButton";
import { ChatDossierInfo } from "./ChatDossierInfo";

const { Text } = Typography;

export const ChatConversationView = ({
  conversationId,
}: {
  conversationId: string;
}) => {
  const conversationQuery = useChatConversation(conversationId);
  const markHandledMutation = useMarkChatConversationHandled();
  const markUnreadMutation = useMarkChatConversationUnread();
  const archiveMutation = useArchiveChatConversation();
  const unarchiveMutation = useUnarchiveChatConversation();
  const unlinkMutation = useUnlinkChatConversation();
  const moveMutation = useMoveChatConversation();

  if (conversationQuery.isLoading) {
    return (
      <Flex align="center" justify="center" style={{ height: "100%" }}>
        <Spin />
      </Flex>
    );
  }

  const conversation = conversationQuery.data;

  if (!conversation) {
    return <Alert type="error" showIcon message="Gesprek niet gevonden." />;
  }

  const channelConfig = chatChannelConfig[conversation.channel];
  const statusConfig = chatConversationStatusConfig[conversation.status];
  const displayName = conversation.contactName ?? conversation.contactNumber;
  const canArchive = conversation.dossierId === null && !conversation.archived;
  const canUnarchive = conversation.archived;
  const canMarkUnread =
    !conversation.unhandled &&
    !conversation.archived &&
    conversation.lastInboundAt !== null;

  return (
    <Flex style={{ height: "100%" }} gap={16}>
      <Flex vertical style={{ flex: 1, minWidth: 0 }} gap={12}>
        <Flex align="center" gap={8} wrap>
          <Tooltip title={channelConfig.label}>
            <span style={{ color: `var(--ant-color-${channelConfig.color})` }}>
              {channelConfig.icon}
            </span>
          </Tooltip>
          <Text strong>{displayName}</Text>
          {conversation.contactName ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {conversation.contactNumber}
            </Text>
          ) : null}
          {statusConfig ? (
            <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
          ) : null}
          {conversation.archived ? <Tag>Gearchiveerd</Tag> : null}
        </Flex>

        <div style={{ flex: 1, overflowY: "auto" }}>
          <ChatThread
            channel={conversation.channel}
            contactName={displayName}
            messages={conversation.messages}
          />
        </div>

        <ChatComposer conversation={conversation} />
      </Flex>

      <Flex
        vertical
        gap={8}
        style={{
          width: 260,
          flexShrink: 0,
          borderLeft: "1px solid rgba(0,0,0,0.06)",
          paddingLeft: 16,
        }}
      >
        {conversation.handledByName ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Opgepakt door {conversation.handledByName}
            {conversation.handledAt
              ? ` op ${dayjs(conversation.handledAt).format("D MMM HH:mm")}`
              : ""}
          </Text>
        ) : null}

        {conversation.status === "needs_review" &&
        conversation.suggestions.length > 0 ? (
          <Flex vertical gap={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Waarschijnlijk dossier
            </Text>
            {conversation.suggestions.map((suggestion) => (
              <Flex
                key={suggestion.dossierId}
                vertical
                gap={4}
                style={{
                  border: "1px solid var(--ant-color-warning-border)",
                  background: "var(--ant-color-warning-bg)",
                  borderRadius: 6,
                  padding: 8,
                }}
              >
                <Text strong style={{ fontSize: 12 }}>
                  {suggestion.driverName ?? "Onbekende bestuurder"}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {[suggestion.licensePlate, `#${suggestion.dossierNumber}`]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
                <Button
                  size="small"
                  type="primary"
                  loading={moveMutation.isPending}
                  onClick={() =>
                    moveMutation.mutate(
                      {
                        conversationId: conversation.id,
                        dossierId: suggestion.dossierId,
                      },
                      {
                        onSuccess: () =>
                          message.success("Gekoppeld aan dossier"),
                      },
                    )
                  }
                >
                  Koppel aan dit dossier
                </Button>
              </Flex>
            ))}
            <Divider style={{ margin: "4px 0" }} />
          </Flex>
        ) : null}

        {conversation.unhandled ? (
          <Button
            block
            icon={<CheckOutlined />}
            loading={markHandledMutation.isPending}
            onClick={() =>
              markHandledMutation.mutate(conversation.id, {
                onSuccess: () => message.success("Gemarkeerd als gezien"),
              })
            }
          >
            Markeer als gezien
          </Button>
        ) : null}

        {canMarkUnread ? (
          <Button
            block
            icon={<RedoOutlined />}
            loading={markUnreadMutation.isPending}
            onClick={() =>
              markUnreadMutation.mutate(conversation.id, {
                onSuccess: () => message.success("Teruggezet naar ongelezen"),
              })
            }
          >
            Markeer als ongelezen
          </Button>
        ) : null}

        <ChatMoveDossierButton
          conversationId={conversation.id}
          isLinked={conversation.dossierId !== null}
          block
        />

        {conversation.dossierId ? (
          <Popconfirm
            title="Ontkoppelen van dossier"
            description="Weet je dit zeker? Het gesprek komt terug in de review-inbox."
            okText="Ontkoppelen"
            cancelText="Annuleren"
            onConfirm={() =>
              unlinkMutation.mutate(conversation.id, {
                onSuccess: () => message.success("Ontkoppeld van dossier"),
              })
            }
          >
            <Button
              block
              icon={<DisconnectOutlined />}
              loading={unlinkMutation.isPending}
            >
              Ontkoppel van dossier
            </Button>
          </Popconfirm>
        ) : null}

        {canArchive ? (
          <Popconfirm
            title="Gesprek archiveren"
            description="Weet je dit zeker?"
            okText="Archiveren"
            cancelText="Annuleren"
            onConfirm={() =>
              archiveMutation.mutate(conversation.id, {
                onSuccess: () => message.success("Gesprek gearchiveerd"),
              })
            }
          >
            <Button
              block
              icon={<InboxOutlined />}
              loading={archiveMutation.isPending}
            >
              Archiveren
            </Button>
          </Popconfirm>
        ) : null}

        {canUnarchive ? (
          <Button
            block
            icon={<UndoOutlined />}
            loading={unarchiveMutation.isPending}
            onClick={() =>
              unarchiveMutation.mutate(conversation.id, {
                onSuccess: () => message.success("Uit archief gehaald"),
              })
            }
          >
            Uit archief halen
          </Button>
        ) : null}

        {conversation.dossierId ? (
          <>
            <Divider style={{ margin: "8px 0" }} />
            <ChatDossierInfo dossierId={conversation.dossierId} />
          </>
        ) : null}
      </Flex>
    </Flex>
  );
};
